import React, { useState, useEffect } from 'react';
import { Button, Form, Message } from '@bedrock/components';
import { Check, Save } from '@bedrock/icons-react';
import classNames from 'classnames/bind';
import SFormItem from '@mpc/common/src/components/SForm/SFormItem';
import DescriptionsFormItem from '@mpc/common/src/components/DescriptionsFormItem';
import ContainerDrawer from '@mpc/common/src/components/ContainerDrawer';
import { ProCard, useScreenAdapterContext } from '@bedrock/eed-react';
import styles from './index.less';
import {
  demandSourceMatchTemplateCreate,
  DemandSourceMatchTemplateCreateForm,
  demandSourceMatchTemplateDetail,
  demandSourceMatchTemplateUpdate,
} from '#services/api';
import CustomInspectionFields from './CustomInspectionFields';

const cx = classNames.bind(styles);

interface DetailsProps {
  detailsId?: number;
  type: 'add' | 'edit' | 'preview';
  updatePage: () => void;
  setDetailsVisible: (visible: boolean) => void;
  visible: boolean;
  mainPlatformOptions: any[];
  productOptions: any[];
}

const CustomInspectionDetail: React.FC<DetailsProps> = ({
  detailsId,
  type,
  updatePage,
  setDetailsVisible,
  visible,
  mainPlatformOptions,
  productOptions,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { validateFields, setFieldsValue, resetFields, getFieldsValue } = form;

  useEffect(() => {
    if (detailsId && type !== 'add') {
      demandSourceMatchTemplateDetail({
        id: detailsId,
      })
        .then((res: any) => {
          if (res.code === 1) {
            const data = res.data.demand_source_match_template;
            form.setFieldsValue({
              main_platform: data.main_platform,
              product_id: data.product_id,
              name: data.name,
              state: data.state,
              demand_source_match_template_field_entities:
                data.demand_source_match_template_field_entities || [],
            });
          }
        })
        .catch((error: any) => {
          console.error('获取详情失败:', error);
        });
    } else {
      form.setFieldsValue({
        state: true,
        demand_source_match_template_field_entities: [{}],
      });
    }
  }, [detailsId, type, form]);

  const handleSubmit = async () => {
    try {
      const values = await validateFields();
      setLoading(true);
      const params: DemandSourceMatchTemplateCreateForm = {
        name: values.name,
        main_platform: values.main_platform,
        product_id: values.product_id,
        state: values.state,
        demand_source_match_template_field_entities:
          values.demand_source_match_template_field_entities.map(
            (item: any) => ({
              field_name: item.field_name,
              plan_remark: item.plan_remark,
              value_example: item.value_example,
            })
          ),
      };

      if (type === 'add') {
        await demandSourceMatchTemplateCreate(params);
      } else {
        await demandSourceMatchTemplateUpdate({
          id: detailsId,
          ...params,
        });
      }

      Message.success(type === 'add' ? '提交成功' : '保存成功');
      updatePage();
      setDetailsVisible(false);
    } catch (error) {
      console.error('提交失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setDetailsVisible(false);
  };

  return (
    <ContainerDrawer
      visible={visible}
      title={
        type === 'add'
          ? '新增定制排查模板'
          : type === 'edit'
          ? '编辑定制排查模板'
          : '预览定制排查模板'
      }
      action={[
        type !== 'preview' && (
          <Button
            type="primary"
            icon={type === 'add' ? <Check /> : <Save />}
            loading={loading}
            onClick={handleSubmit}
          >
            {type === 'add' ? '提交' : '保存'}
          </Button>
        ),
      ].filter(Boolean)}
      onClose={handleCancel}
      destroyOnClose
      hiddenSearchBar
      closable
    >
      <div className={cx('create__body')}>
        <Form
          form={form}
          layout="vertical"
          showLabelColon
          preserve={false}
          initialValues={{
            state: true,
            demand_source_match_template_field_entities: [{}],
          }}
        >
          <ProCard title={'基本信息'} level={1}>
            <div className={cx('collapse-container-content')}>
              <DescriptionsFormItem
                column={2}
                lightBorder
                items={[
                  {
                    formItemKey: 'main_platform',
                    label: '合作主平台',
                    labelStyle: { lineHeight: '27px' },
                    componentClass: 'select',
                    required: true,
                    span: 1,
                    elementProps: {
                      multiple: true,
                      placeholder: '请选择',
                      options: mainPlatformOptions,
                      disabled: type === 'preview',
                      showReset: true,
                      showConfirm: true,
                    },
                  },
                  {
                    formItemKey: 'product_id',
                    label: '产品',
                    labelStyle: { lineHeight: '27px' },
                    componentClass: 'select',
                    required: true,
                    span: 1,
                    elementProps: {
                      multiple: true,
                      placeholder: '请选择',
                      options: productOptions,
                      disabled: type === 'preview',
                      returnOnlyLeaf: true,
                      showReset: true,
                      showConfirm: true,
                    },
                  },
                  {
                    formItemKey: 'name',
                    label: '模板名称',
                    labelStyle: { lineHeight: '27px' },
                    componentClass: 'input',
                    required: true,
                    span: 2,
                    elementProps: {
                      placeholder: '请输入',
                      maxLength: 20,
                      showCount: true,
                      disabled: type === 'preview',
                    },
                  },
                  {
                    formItemKey: 'state',
                    label: '状态',
                    labelStyle: { lineHeight: '27px' },
                    componentClass: 'radio',
                    required: true,
                    span: 2,
                    elementProps: {
                      className: 'mt-[2px]',
                      options: [
                        { name: '启用', value: true },
                        { name: '禁用', value: false },
                      ],
                      disabled: type === 'preview',
                    },
                  },
                ]}
              />
            </div>
          </ProCard>
          <ProCard title={'定制排查字段'} level={1}>
            <div className={cx('collapse-container-content')}>
              <SFormItem
                noStyle
                className={cx('custom-form-item-input')}
                formItemKey="demand_source_match_template_field_entities"
                watchChildFormValidate={true}
                validateTrigger={['onChange, onSubmit']}
                render={() => <CustomInspectionFields type={'edit'} />}
              />
            </div>
          </ProCard>
        </Form>
      </div>
    </ContainerDrawer>
  );
};

export default CustomInspectionDetail;
